import {Alert, Button, FormControl, FormField, showNotification} from 'platform/components';
import {Box, Hide, Show, Stack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isEmpty, isNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import {suffixTestId, TestIdProps, useRequiredParams} from 'shared/web';

import {validateVin} from 'features/vin-validate';

import i18n from '../../../i18n/i18n';
import {
  useGetRequestQuery,
  useLazyGetRequestVinDetailsPaidQuery,
  useLazyGetRequestVinDetailsQuery,
  useUpdateRequestMutation,
} from '../../../store/priceReportApi';
import {DRIVE_4X2} from '../constants/drive4x2';
import {DRIVE_4X4} from '../constants/drive4x4';
import {VehicleFormType} from '../types/VehicleFormType';
import {useGetRequestVehicleFromFormData} from '../utils/useGetRequestVehicleFromFormData';

interface VinParserProps extends TestIdProps {
  control: FormControl<VehicleFormType>;
  formApi: UseFormReturn<VehicleFormType>;
}

export function VinParser(props: VinParserProps) {
  const {requestId} = useRequiredParams();
  const getRequestVehicleFromFormData = useGetRequestVehicleFromFormData();

  const {data: request} = useGetRequestQuery({id: requestId ?? ''});
  const [updateRequest] = useUpdateRequestMutation();
  const [lastDecodeCall, setLastDecodeCall] = useState<'free' | 'paid' | null>(null);
  const [getRequestVinDetails, {isLoading: isLoadingFree, isError: isErrorFree, data: dataFree}] =
    useLazyGetRequestVinDetailsQuery();
  const [
    getRequestVinDetailsPaid,
    {isLoading: isLoadingPaid, isError: isErrorPaid, data: dataPaid},
  ] = useLazyGetRequestVinDetailsPaidQuery();
  const isLoading = isLoadingFree || isLoadingPaid;
  const isError = isErrorFree || isErrorPaid;
  const data = match(lastDecodeCall)
    .with('free', () => dataFree)
    .with('paid', () => dataPaid)
    .otherwise(() => null);

  const handleDecodeVin = async (isPaid: boolean = false) => {
    const vin = props.formApi.getValues('vin');

    if (isNil(vin) || isEmpty(vin)) {
      return props.formApi.setError('vin', {
        message: i18n.t('page.create.validation.fieldIsRequired'),
      });
    }

    if (!validateVin(vin)) {
      return props.formApi.setError('vin', {
        message: i18n.t('page.create.validation.vinIsInvalid'),
      });
    }

    const decodedData = await (isPaid ? getRequestVinDetailsPaid : getRequestVinDetails)({
      id: requestId,
    })
      .unwrap()
      .catch(() => showNotification.error(i18n.t('general.labels.error')));
    setLastDecodeCall(isPaid ? 'paid' : 'free');

    if (decodedData?.make) {
      props.formApi.setValue('make', decodedData.make);
    }
    if (decodedData?.modelFamily) {
      props.formApi.setValue('modelFamily', decodedData.modelFamily);
    }
    if (decodedData?.firstRegistrationOnYear) {
      props.formApi.setValue(
        'firstRegistrationOnYear',
        parseInt(decodedData.firstRegistrationOnYear)
      );
    }
    if (decodedData?.transmission) {
      props.formApi.setValue('transmission', [decodedData.transmission]);
    }
    if (decodedData?.fuelType) {
      props.formApi.setValue('fuelType', [decodedData.fuelType]);
    }
    if (decodedData?.bodyStyle) {
      props.formApi.setValue('bodyStyle', [decodedData.bodyStyle]);
    }
    if (decodedData?.drive) {
      props.formApi.setValue('drive', [decodedData.drive === DRIVE_4X4 ? DRIVE_4X4 : DRIVE_4X2]);
    }
    if (decodedData?.power) {
      props.formApi.setValue('power', parseInt(decodedData.power));
    }
    if (decodedData?.maximalPower) {
      props.formApi.setValue('maxPower', parseInt(decodedData.maximalPower));
    }
    if (decodedData?.exteriorColor) {
      props.formApi.setValue('exteriorColor', [decodedData.exteriorColor]);
    }
    if (decodedData?.seatCount) {
      props.formApi.setValue('seatCount', [decodedData.seatCount]);
    }
    if (decodedData?.doorCount) {
      props.formApi.setValue('doorCount', [decodedData.doorCount]);
    }
    if (decodedData?.features) {
      props.formApi.setValue('features', decodedData.features);
    }

    await updateRequest({
      id: requestId,
      body: {vehicle: getRequestVehicleFromFormData(props.formApi.getValues())},
    })
      .unwrap()
      .catch(() => showNotification.error(i18n.t('general.labels.error')));
  };

  const isDecodedSuccesfully =
    data?.decodingStatus === 'partially' || data?.decodingStatus === 'fully';
  const isDecodedUnsuccesfully = data?.decodingStatus === 'none' || isError;

  return (
    <VStack spacing={6}>
      <Stack
        direction={['column', 'column', 'row', 'row']}
        spacing={6}
        align={['stretch', 'stretch', 'flex-end']}
      >
        <Box flex={1}>
          <FormField
            control={props.control}
            type="text"
            name="vin"
            label={`${i18n.t('page.create.labels.vin')} (${i18n.t('general.labels.optional')})`}
            onChange={() => props.formApi.clearErrors('vin')}
            data-testid={suffixTestId('vin', props)}
          />
        </Box>
        <Hide when={request?.additionalSettings?.hideVinDecoder}>
          <Button
            title={i18n.t('page.create.labels.loadDataByVIN')}
            onClick={() => handleDecodeVin(false)}
            isLoading={isLoading}
            isDisabled={isTrue(request?.vinDecoded)}
            data-testid={suffixTestId('loadDataByVIN', props)}
          />
        </Hide>
        <Show when={request?.additionalSettings?.showVinDecoderPaid}>
          <Button
            title={i18n.t('page.create.labels.loadDataByVINPaid')}
            onClick={() => handleDecodeVin(true)}
            isLoading={isLoading}
            isDisabled={isTrue(request?.vinDecodedPaid)}
            data-testid={suffixTestId('loadDataByVINPaid', props)}
          />
        </Show>
      </Stack>
      <Show when={isDecodedSuccesfully}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('page.create.labels.vinCodeRecognized')}
          message={i18n.t('page.create.labels.vinCodeRecognizedMessage')}
          data-testid={suffixTestId('vinCodeRecognized', props)}
        />
      </Show>
      <Show when={isDecodedUnsuccesfully}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('page.create.labels.vinCodeNotRecognized')}
          message={i18n.t('page.create.labels.vinCodeNotRecognizedMessage')}
          data-testid={suffixTestId('vinCodeNotRecognized', props)}
        />
      </Show>
    </VStack>
  );
}
